import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../providers/AuthProvider';
import { getUserTier, useAuthStore, type UserTier } from '../stores/auth.store';
import { cn } from '../lib/utils';
import { Button } from './ui';

/**
 * Returns the navigation links appropriate for the given user tier.
 * - guest: only "Meet the Agents"
 * - member: "Meet the Agents" + "Community"
 * - early_tester: "Meet the Agents" + "Community" + "My Dashboard"
 */
export function getNavLinks(tier: UserTier): Array<{ label: string; to: string }> {
  switch (tier) {
    case 'guest':
      return [
        { label: 'Meet the Agents', to: '/agents' },
        { label: 'Start Here', to: '/evolve' },
        { label: 'Appreciative Language', to: '/appreciative-language' },
        { label: 'Privacy', to: '/privacy' },
        { label: 'Contact Us', to: '/contact' },
      ];
    case 'member':
      return [
        { label: 'Meet the Agents', to: '/agents' },
        { label: 'Start Here', to: '/evolve' },
        { label: 'Community', to: '/community' },
        { label: 'Appreciative Language', to: '/appreciative-language' },
        { label: 'Privacy', to: '/privacy' },
        { label: 'Contact Us', to: '/contact' },
      ];
    case 'early_tester':
      return [
        { label: 'Meet the Agents', to: '/agents' },
        { label: 'Start Here', to: '/evolve' },
        { label: 'Community', to: '/community' },
        { label: 'My Dashboard', to: '/workspace' },
        { label: 'Appreciative Language', to: '/appreciative-language' },
        { label: 'Privacy', to: '/privacy' },
        { label: 'Contact Us', to: '/contact' },
      ];
  }
}

/**
 * Main navigation bar.
 * - Guests see: logo + "Meet the Agents" link + "Join Us" CTA
 * - Members see: logo + "Meet the Agents" + "Community" + notification bell + user menu
 * - Early Testers see: logo + "Meet the Agents" + "Community" + "My Dashboard" + notification bell + user menu
 * - Responsive: hamburger menu on mobile
 */
export function NavBar(): React.JSX.Element {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const tier = getUserTier(useAuthStore());
  const navLinks = getNavLinks(tier);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-surface-raised/95 backdrop-blur supports-[backdrop-filter]:bg-surface-raised/80">
      <nav className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo / Wordmark */}
        <Link to="/" className="flex flex-col">
          <span className="text-lg font-semibold text-[#611F69]">AppreciativeAI</span>
          <span className="text-[10px] text-[#611F69]">Grow What Fuels You at Work</span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden items-center gap-6 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm font-bold text-gail transition-colors hover:opacity-80"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <>
              {/* Notification bell */}
              <button
                type="button"
                className="relative rounded-full p-2 text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                aria-label="Notifications"
                onClick={() => navigate('/notifications')}
              >
                <BellIcon />
                {/* Unread badge placeholder — connect to notification store later */}
              </button>

              {/* User menu */}
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-full p-1.5 text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  {user?.photo_url ? (
                    <img
                      src={user.photo_url}
                      alt=""
                      className="h-7 w-7 rounded-full object-cover"
                    />
                  ) : (
                    <UserIcon />
                  )}
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg border border-border bg-surface-raised py-1 shadow-lg">
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Settings
                    </Link>
                    <button
                      type="button"
                      className="block w-full px-4 py-2 text-left text-sm text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                      onClick={handleLogout}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Guest: Join Us CTA */}
              <Link to="/login" className="hidden text-sm font-bold text-yves hover:opacity-80 md:block">
                Log in
              </Link>
              <Button size="sm" onClick={() => navigate('/register')}>
                Join Us
              </Button>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            className="inline-flex items-center rounded-md p-2 text-text-secondary hover:bg-surface-muted md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <XIcon /> : <MenuIcon />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-border bg-surface-raised px-4 py-3 md:hidden">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="rounded-md px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                Log in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

/* ─── Inline SVG icons (lightweight, no external deps) ───────────────── */

function BellIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
